import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { AutoCoderImportBatch, AutoCoderImportBatchList, AutoCoderPipeline } from '../types/gotypes'
import { extractErrorMessage } from '../utils/apitools'
import { importBatchIsInFlight, isImportRequest, type ImportTab } from '../utils/importBatches'

/**
 * The JOB-wide receipts ledger (`GET /autocoder/:customer/:job/import-batches`).
 *
 * After the ingest detach a receipt normally has NO pipeline, so the
 * pipeline-scoped listing cannot see most of them: the job is the scope, and
 * this is the only reader that shows a job everything it has fetched.
 *
 * Paged server-side (a connector writes a receipt per tick, so the list is
 * never fetched whole) and `enabled`-gated, because a hidden tab must not poll.
 */

/** Prefix of every receipts-list key, so a mutation can invalidate them all. */
export const JOB_RECEIPTS_QUERY_KEY = 'autocoder-job-receipts'

/**
 * How often the list refreshes. A settled ledger changes at connector pace, so
 * 30s is plenty; a list holding an unfinalized receipt is a list that is still
 * being written, and gets watched at 10s until the last marker lands.
 */
export const RECEIPTS_POLL_SETTLED_MS = 30 * 1000
export const RECEIPTS_POLL_IN_FLIGHT_MS = 10 * 1000

export const receiptsPollInterval = (data: AutoCoderImportBatchList | undefined): number => {
  const now = Date.now()
  return (data?.batches || []).some(b => importBatchIsInFlight(b, now))
    ? RECEIPTS_POLL_IN_FLIGHT_MS
    : RECEIPTS_POLL_SETTLED_MS
}

export interface UseJobReceiptsResult {
  data: AutoCoderImportBatchList | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

export const useJobReceipts = (
  customer: string,
  job: string,
  page: number,
  rowsPerPage: number,
  enabled: boolean,
  // The Imports panel's tab (design/2026-10-22-import-queue.md): '' is every
  // batch but the waiting requests, the receipts list as it always was.
  tab: ImportTab = '',
): UseJobReceiptsResult => {
  const query = useQuery<AutoCoderImportBatchList, Error>({
    queryKey: [JOB_RECEIPTS_QUERY_KEY, customer, job, page, rowsPerPage, tab],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
      })
      if (tab) params.set('status', tab)
      try {
        const response = await axios.get<AutoCoderImportBatchList>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/import-batches?${params.toString()}`,
        )
        return response.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    enabled: enabled && !!customer && !!job,
    refetchInterval: query => receiptsPollInterval(query.state.data),
    refetchIntervalInBackground: false,
    staleTime: 5 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? extractErrorMessage(query.error) : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

/**
 * Replaying a fetch (`POST /ingest/retry`): the server reads the receipt's
 * stored query and sends it again as a NEW receipt — the old one is never
 * reopened, which is why the list is what gets invalidated, not one row.
 *
 * Only apify and carbon receipts carry a replayable request; a CSV receipt
 * names a file the server no longer holds and the endpoint answers 400. The
 * table therefore offers this only where `receiptIsRetryable` says so.
 */
export const RETRYABLE_RECEIPT_SOURCES = ['apify', 'carbon']

export const receiptIsRetryable = (batch: AutoCoderImportBatch): boolean =>
  !isImportRequest(batch) && RETRYABLE_RECEIPT_SOURCES.includes((batch?.source || '').trim())

/**
 * A request on the import queue is retried and cancelled through the queue
 * (`POST .../import-batches/:id/retry` | `/cancel`, Manager only): a failed
 * request is queued again now under its driver's limits, a waiting one is
 * withdrawn. Never through `/ingest/retry`, which would be an uncapped fetch.
 */
export const useImportRequestAction = (customer: string, job: string) => {
  const queryClient = useQueryClient()
  const mutation = useMutation<void, Error, { id: string; action: 'retry' | 'cancel' }>({
    mutationFn: async ({ id, action }) => {
      try {
        await axios.post(`${API_BASE_URL}/autocoder/${customer}/${job}/import-batches/${id}/${action}`)
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [JOB_RECEIPTS_QUERY_KEY] })
    },
  })
  return {
    act: (id: string, action: 'retry' | 'cancel') => mutation.mutateAsync({ id, action }),
    actingId: mutation.isPending ? mutation.variables?.id ?? null : null,
    error: mutation.error ? mutation.error.message : null,
  }
}

export interface UseRetryReceiptResult {
  retry: (batch: AutoCoderImportBatch) => Promise<AutoCoderImportBatch | undefined>
  retryingId: string | null
  error: string | null
}

export const useRetryReceipt = (customer: string, job: string): UseRetryReceiptResult => {
  const queryClient = useQueryClient()

  const mutation = useMutation<AutoCoderImportBatch | undefined, Error, AutoCoderImportBatch>({
    mutationFn: async batch => {
      try {
        const response = await axios.post<{ batch: AutoCoderImportBatch }>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/ingest/retry`,
          { importBatchId: batch.id },
        )
        return response.data?.batch
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: () => {
      // Prefix invalidation: the retry cannot know which page/size the caller is
      // looking at, and the new receipt lands at the top of every one of them.
      void queryClient.invalidateQueries({ queryKey: [JOB_RECEIPTS_QUERY_KEY] })
    },
  })

  return {
    retry: batch => mutation.mutateAsync(batch),
    retryingId: mutation.isPending ? mutation.variables?.id ?? null : null,
    error: mutation.error ? mutation.error.message : null,
  }
}

/**
 * id → name for the receipts table's Pipeline column. Blank is the normal
 * reading (a detached receipt has no pipeline), so this is only ever a
 * decoration on the minority of rows that do — one cached list request, not one
 * per row.
 */
export const usePipelineNames = (customer: string, job: string): Record<string, string> => {
  const query = useQuery<Record<string, string>, Error>({
    queryKey: ['autocoder-pipeline-names', customer, job],
    queryFn: async () => {
      const res = await axios.get<{ items: AutoCoderPipeline[] }>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines`,
        { params: { limit: 500, offset: 0, lifecycle: 'any' } },
      )
      const map: Record<string, string> = {}
      ;(res.data?.items || []).forEach(p => {
        // A pipeline's display name lives on its config; system_name is the
        // connector's handle for it and the only name a scaffolded pipeline has.
        if (p.id) map[p.id] = p.config?.name || p.system_name || p.id
      })
      return map
    },
    enabled: !!customer && !!job,
    // Names change when someone renames a pipeline, not while a ledger is open.
    staleTime: 5 * 60 * 1000,
  })

  return query.data || {}
}
